<template>
  <ui-modal
    v-model="show"
    :closable="false"
    class="cropper-modal"
    @before-open="reset"
  >
    <template #title>
      {{ title }}
    </template>
    <template #footer>
      <div class="d-flex justify-content-around w-100">
        <ui-button
          outline
          size="sm"
          type="primary"
          native-type="button"
          class="px-4"
          @click="handleClose"
        >
          Close
        </ui-button>
        <ui-button
          size="sm"
          type="primary"
          native-type="button"
          class="px-4"
          :disabled="!croppedFile"
          @click="handleApply"
        >
          Save
        </ui-button>
      </div>
    </template>

    <ui-input
      v-if="aspects.length"
      :value="cropperFormat"
      label="Select cropper format"
      class="text-center cropper-modal__format"
    >
      <div class="cropper-modal__format">
        <ui-button
          v-for="item in aspects"
          native-type="button"
          :key="item.label"
          size="sm"
          :type="isSelectedFormat(item) ? 'primary' : 'default'"
          outline
          @click="handleFormat(item)"
        >
          {{ item.label }}
        </ui-button>
      </div>
    </ui-input>

    <cropper
      v-bind="cropperSettings"
      :src="image"
      class="cropper-modal__cropper"
      @change="handleChange"
    ></cropper>
  </ui-modal>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper';
import toBlob from '@/utils/toBlob';
import resetMixin from '@/mixins/reset-mixin';

export default {
  components: {
    Cropper,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    settings: {
      type: Object,
      required: false,
    },
    aspects: {
      type: Array,
      required: false,
      default: () => [],
    },
    image: {
      type: [Object, String, Blob],
      required: false,
      default: () => null,
    },
    type: {
      type: String,
      required: false,
      default: '',
    },
  },

  mixins: [
    resetMixin(() => ({
      croppedFile: null,
      cropperFormat: 1,
    })),
  ],

  data() {
    return {
      show: false,
    };
  },

  computed: {
    cropperSettings() {
      if (this.aspects.length) {
        return {
          ...this.settings,
          stencilProps: {
            aspectRatio: this.cropperFormat,
          },
        };
      }

      return this.settings;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (value !== this.show) {
          this.show = value;
        }
      },
    },
    show(show) {
      if (show !== this.value) {
        this.$emit('input', show);
      }
    },
  },

  methods: {
    handleClose() {
      this.show = false;
      this.$emit('closed');
    },
    handleApply() {
      this.show = false;
      this.$emit('apply', this.croppedFile);
    },
    async handleChange({ canvas }) {
      try {
        this.croppedFile = await toBlob(canvas, this.type);
        this.$emit('change', this.croppedFile);
      } catch {
        this.$showServerError({
          message: 'Can\'t crop selected photo!',
        });
      }
    },
    isSelectedFormat(item) {
      return this.cropperFormat === item.aspectRatio;
    },
    handleFormat(item) {
      this.cropperFormat = item.aspectRatio;
    },
  },
};
</script>

<style lang="scss">
.design .cropper-modal {
  &__format {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 10px;
    margin-bottom: 0;
  }

  &__cropper {
    height: 50vh;
    background: #fff;
  }
}
</style>
